import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Doc as DocComponent, DownloadButton, Signer } from '@app/common';
import { Grid, Switch } from '@app/components';
import { Doc, DocStatus, DocStatusName, UserType } from '@app/models';
import { blobToBase64, fullName } from '@app/helpers';
import { useNotification, useUserContext } from '@app/providers';
import { getFileById, updateDoc } from '@app/api';
import { Button } from 'components/ui/button';
import Loader from 'components/Loader';
// @ts-ignore
import { NCALayerClient } from 'ncalayer-js-client';
import {
  parseNcaKeyInfoResponse,
  verifyEcpMatchesContractParty,
} from '../../../../libs';

interface Props {
  doc: Doc;
  defaultChecked: boolean;
  updateDoc: (doc: Partial<Doc>) => void;
  checkIin?: () => void;
}

function ContractDoc(props: Props) {
  const { doc, defaultChecked, updateDoc: updateDocProps, checkIin } = props;
  const [checked, setChecked] = useState<boolean>(defaultChecked);

  const { user, company } = useUserContext();
  const { showNotification } = useNotification();
  const [pending, setPending] = useState<boolean>(false);

  const ncaLayerClient = useMemo(() => new NCALayerClient(), []);

  const ownerSigned = useMemo(
    () => [DocStatus.OWNER_SIGNED, DocStatus.SIGNED].indexOf(doc.status) > -1,
    [doc]
  );

  const recipientSigned = useMemo(
    () =>
      [DocStatus.RECIPIENT_SIGNED, DocStatus.SIGNED].indexOf(doc.status) > -1,
    [doc]
  );

  const fileId = useMemo<string | null>(() => {
    try {
      return doc?.fileId as string;
    } catch (e) {
      return null;
    }
  }, [doc]);

  const onChangeSwitcher = useCallback((value: boolean) => {
    setChecked(value);
  }, []);

  const onClickSign = useCallback(async () => {
    try {
      if (checkIin && (!doc?.owner?.iin || !doc?.recipient?.iin)) {
        checkIin();
        return;
      }
      if (!ncaLayerClient.wsConnection) {
        await ncaLayerClient.connect();
      }
    } catch (error) {
      showNotification({
        variant: 'error',
        message: 'NCALayer не подключен! Подключите и попробуйте снова',
      });

      return;
    }

    try {
      if (ncaLayerClient.wsConnection.readyState !== 1) {
        ncaLayerClient.wsConnection.close();

        showNotification({
          variant: 'error',
          message: 'NCALayer не подключен! Подключите и попробуйте снова',
        });

        return;
      }

      setPending(true);

      const keyInfo = await ncaLayerClient.getKeyInfo(
        NCALayerClient.fileStorageType
      );
      const { dn, expired } = parseNcaKeyInfoResponse(keyInfo);
      if (expired) {
        showNotification({
          variant: 'error',
          message: 'Срок действия сертификата ЭЦП истёк.',
        });
        setPending(false);
        return;
      }
      if (!dn) {
        showNotification({
          variant: 'error',
          message: 'Не удалось прочитать данные сертификата ЭЦП.',
        });
        setPending(false);
        return;
      }

      const ecpCheck = verifyEcpMatchesContractParty(dn, {
        userType: user.type,
        user,
        company: company ?? null,
        contract: doc,
      });
      if (!ecpCheck.ok) {
        showNotification({
          variant: 'error',
          message: ecpCheck.message,
        });
        setPending(false);
        return;
      }

      const blobContractFile = await getFileById(doc.fileId);
      const base64ContractFile = await blobToBase64(blobContractFile);

      const signResponse = await ncaLayerClient.createCAdESFromBase64(
        NCALayerClient.fileStorageType,
        base64ContractFile,
        'SIGNATURE',
        false
      );

      const docData: Partial<Doc> = {
        id: doc.id,
      };

      if (user.type === UserType.INDIVIDUAL) {
        if (user.id === doc.recipientId) {
          docData.recipientSign = signResponse;
          docData.status = DocStatus.RECIPIENT_SIGNED;
        } else {
          docData.ownerSign = signResponse;
          docData.status = DocStatus.OWNER_SIGNED;
        }
      } else {
        if (company!.id === doc.recipientCompanyId) {
          docData.recipientSign = signResponse;
          docData.status = DocStatus.RECIPIENT_SIGNED;
        } else {
          docData.ownerSign = signResponse;
          docData.status = DocStatus.OWNER_SIGNED;
        }
      }

      const docResponse = await updateDoc(docData);

      if (!!updateDocProps) {
        updateDocProps({
          id: docResponse.id,
          status: docResponse.status,
        });
      }

      setPending(false);
    } catch (e: unknown) {
      setPending(false);
      const err = e as { canceledByUser?: boolean };
      if (err?.canceledByUser) {
        return;
      }
      showNotification({
        variant: 'error',
        message:
          'Не удалось подписать документ. Проверьте NCALayer и сертификат.',
      });
    }
  }, [
    checkIin,
    ncaLayerClient,
    updateDocProps,
    showNotification,
    doc,
    user,
    company,
  ]);

  const control = useMemo(() => {
    if (doc.status === DocStatus.SIGNED) {
      return [
        <DownloadButton
          text="Скачать договор"
          fileId={fileId}
          variant="outlined"
        />,
      ];
    }

    if (user.type === UserType.INDIVIDUAL) {
      if (user.id === doc.recipientId && recipientSigned) {
        return null;
      }

      if (user.id === doc.ownerId && ownerSigned) {
        return null;
      }
    } else {
      if (company!.id === doc.recipientCompanyId && recipientSigned) {
        return null;
      }

      if (company!.id === doc.companyId && ownerSigned) {
        return null;
      }
    }

    return [
      <Button onClick={onClickSign} disabled={pending}>
        {pending && (
          <span className="mr-2">
            <Loader size="small" color="default" />
          </span>
        )}
        Подписать
      </Button>,
    ];
  }, [
    fileId,
    user,
    onClickSign,
    pending,
    doc,
    recipientSigned,
    ownerSigned,
    company,
  ]);

  useEffect(() => {
    return () => {
      if (ncaLayerClient.wsConnection) {
        ncaLayerClient.wsConnection.close();
      }
    };
  }, [ncaLayerClient]);

  return (
    <div className="bg-gray-100 rounded-lg p-3 sm:p-6">
      <div className="flex justify-between items-center gap-2 mb-3">
        <h2 className="text-base sm:text-xl font-medium text-gray-800 break-words flex-1 min-w-0">{`${
          doc.name
        }, ${!!doc.companyId ? doc.company!.name : fullName(doc.owner)}`}</h2>
        <Switch checked={checked} onChange={onChangeSwitcher} />
      </div>
      {checked && (
        <div className="space-y-4 sm:space-y-6">
          <DocComponent
            doc={doc}
            variant="secondary"
            hideStatus={true}
            hideName={true}
          />
          <Grid columns={2} gap={16}>
            <Signer
              signedText={DocStatusName[DocStatus.OWNER_SIGNED]}
              signed={ownerSigned}
              user={!doc.companyId ? doc.owner : null}
              company={!!doc.companyId ? doc.company : null}
            />
            <Signer
              signedText={DocStatusName[DocStatus.RECIPIENT_SIGNED]}
              signed={recipientSigned}
              user={!doc.recipientCompanyId ? doc.recipient : null}
              company={!!doc.recipientCompanyId ? doc.recipientCompany : null}
            />
          </Grid>
          <div className="flex justify-end gap-2 sm:gap-4">{control}</div>
        </div>
      )}
    </div>
  );
}

export default memo(ContractDoc);
